<template>
  <div class="col-md-12 mx-auto py-3">
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group label="Report Type">
            <b-form-select v-model="reportType" :options="reportTypeOptions"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="reportType === 'status'">
        <b-col md="6">
          <b-form-group label="Staff">
            <b-form-select v-model="scope" :options="scopeOptions"></b-form-select>
          </b-form-group>
        </b-col>

        <b-col md="6" v-if="scope === 'single'">
          <b-form-group label="Select Staff">
            <multiselect
              v-model="selectedFaculty"
              :options="facultyOptions"
              :searchable="true"
              :loading="loadingFaculty"
              track-by="id"
              label="name"
              placeholder="Search staff..."
              :close-on-select="true"
              :allow-empty="false"
              select-label=""
              selected-label=""
              deselect-label=""
            ></multiselect>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Date Selection">
        <b-form-radio-group v-model="dateMode" :options="dateModeOptions" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
      </b-form-group>

      <b-row v-if="dateMode === 'range'">
        <b-col md="6">
          <b-form-group label="From Date">
            <b-form-datepicker v-model="filters.fromDate" reset-button></b-form-datepicker>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="To Date" :state="toDateState" :invalid-feedback="toDateFeedback">
            <b-form-datepicker v-model="filters.toDate" :min="filters.fromDate" :max="maxToDate" reset-button></b-form-datepicker>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-else>
        <b-col md="6">
          <b-form-group label="Month">
            <input type="month" v-model="selectedMonth" class="form-control">
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Status" v-if="reportType === 'status'">
        <b-form-checkbox v-model="filters.statuses" value="approved" inline>Approved</b-form-checkbox>
        <b-form-checkbox v-model="filters.statuses" value="rejected" inline>Rejected</b-form-checkbox>
        <b-form-checkbox v-model="filters.statuses" value="pending" inline>Pending</b-form-checkbox>
      </b-form-group>

      <div class="d-flex justify-content-end mt-3" style="gap: 8px;">
        <b-button variant="primary" :disabled="isDisabled || viewing" @click="handleReport('view')">
          <b-spinner v-if="viewing" small class="mr-1"></b-spinner>
          {{ viewing ? 'Loading...' : 'View Report' }}
        </b-button>
        <b-button variant="danger" :disabled="isDisabled || downloading" @click="handleReport('download')">
          <b-spinner v-if="downloading" small class="mr-1"></b-spinner>
          {{ downloading ? 'Downloading...' : 'Download Report' }}
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      viewing: false,
      downloading: false,
      loadingFaculty: false,
      reportType: "count",
      reportTypeOptions: [
        { value: "count", text: "Staff Leave Count Report" },
        { value: "status", text: "Staff Leave Status Report" },
      ],
      scope: "all",
      selectedFaculty: null,
      facultyOptions: [],
      scopeOptions: [
        { value: "all", text: "All" },
        { value: "single", text: "Individual" },
      ],
      dateMode: "range",
      dateModeOptions: [
        { value: "range", text: "Date Range" },
        { value: "month", text: "Month" },
      ],
      selectedMonth: "",
      filters: {
        fromDate: "",
        toDate: "",
        statuses: ["approved"],
      },
    };
  },

  computed: {
    maxToDate() {
      if (!this.filters.fromDate) return null;
      const d = new Date(this.filters.fromDate);
      d.setDate(d.getDate() + 30);
      return d.toISOString().slice(0, 10);
    },

    toDateState() {
      if (!this.filters.fromDate || !this.filters.toDate) return null;
      const diff = (new Date(this.filters.toDate) - new Date(this.filters.fromDate)) / 86400000;
      return diff <= 30 ? null : false;
    },

    toDateFeedback() {
      return "Date range cannot exceed 31 days.";
    },

    isDisabled() {
      if (this.reportType === "status") {
        if (this.filters.statuses.length === 0) return true;
        if (this.scope === "single" && !this.selectedFaculty) return true;
      }
      if (this.dateMode === "range") return !this.filters.fromDate || !this.filters.toDate || this.toDateState === false;
      if (this.dateMode === "month") return !this.selectedMonth;
      return false;
    },
  },

  async created() {
    await this.fetchFaculties();
  },

  methods: {
    async fetchFaculties() {
      this.loadingFaculty = true;
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/institutionusers/faculties`;
        const response = await axios.get(url);
        const users = Array.isArray(response.data) ? response.data : response.data.data ?? [];
        this.facultyOptions = users.map(u => ({
          id: u.id,
          name: `${u.first_name} ${u.last_name}` + (u.email ? ` (${u.email})` : ""),
        }));
      } catch {
        // silently fall back
      } finally {
        this.loadingFaculty = false;
      }
    },

    async handleReport(mode) {
      if (mode === "view") this.viewing = true;
      else this.downloading = true;

      try {
        let fromDate, toDate;
        if (this.dateMode === "month" && this.selectedMonth) {
          const [year, month] = this.selectedMonth.split("-").map(Number);
          const lastDay = new Date(year, month, 0).getDate();
          fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
          toDate = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
        } else {
          fromDate = this.filters.fromDate || null;
          toDate = this.filters.toDate || null;
        }

        const isCount = this.reportType === "count";
        const payload = {
          report_type: this.reportType,
          status: isCount ? null : this.filters.statuses,
          from_date: fromDate,
          to_date: toDate,
          institution_user_id: !isCount && this.scope === "single" && this.selectedFaculty
            ? this.selectedFaculty.id
            : null,
        };

        const url = `${this.$store.getters.getAPIKey.mainAPI}/faculty-leave/report/pdfexport`;
        const response = await axios.post(url, payload, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));

        if (mode === "view") {
          window.open(blobUrl, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", isCount ? "staff_leave_count_report.pdf" : "staff_leave_status_report.pdf");
          document.body.appendChild(link);
          link.click();
          link.remove();
        }

        window.URL.revokeObjectURL(blobUrl);
      } catch {
        this.$toast.error("Failed to generate report", { position: "top", duration: 3000 });
      } finally {
        this.viewing = false;
        this.downloading = false;
      }
    },
  },
};
</script>

<style>
.multiselect .multiselect__content-wrapper {
  z-index: 9999 !important;
}
</style>
